import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { TMS_ENDPOINTS } from '../../constants/tms.const';
import { HttpClientService } from '../httpClient/http-client.service';

export interface CalendarSelfReport {
  eventId: string;
  attendanceStatus: 0 | 1;
  attendanceNote: string;
}

export interface TaskSelfReport {
  taskId: string;
  isCompleted: boolean;
  taskNote: string;
}

export interface SignOffPayload {
  userId: string | number;
  calendarSelfReports?: CalendarSelfReport[];
  taskSelfReports?: TaskSelfReport[];
}

export interface SignInPayload {
  userId: string | number;
  businessAsUsual: boolean;
  startDayDescription: string;
  metadata: Record<string, any>;
}

@Injectable({
  providedIn: 'root',
})
export class TMSService {
  private httpClientService = inject(HttpClientService);

private static readonly _DAY_STARTED_KEY = 'tms_day_started';

  // ── Day started state (persisted across tabs via localStorage) ───
  get dayStarted(): boolean {
    return localStorage.getItem(TMSService._DAY_STARTED_KEY) === 'true';
  }

  markDayStarted(): void {
    localStorage.setItem(TMSService._DAY_STARTED_KEY, 'true');
  }

  reset(): void {
    localStorage.removeItem(TMSService._DAY_STARTED_KEY);
  }

  // ── API methods ───────────────────────────────────────

  /**
   * POST /api/v1/task-management/user-logs/day/sign-in
   * Called on "Start My Day" button click
   */
  signIn(payload: SignInPayload): Observable<any> {
    return this.httpClientService.postTMS(
      TMS_ENDPOINTS.userLogs.signIn,
      payload
    );
  }

  /**
   * POST /api/v1/task-management/user-logs/break/start
   * Called when user goes away (header "Away" button, or auto idle-lock).
   * The break type is no longer picked at start — the user is asked for it
   * when the break ends instead.
   */
  breakStart(userId: string | number): Observable<any> {
    return this.httpClientService.postTMS(
      TMS_ENDPOINTS.userLogs.breakStart,
      { userId }
    );
  }

  /**
   * POST /api/v1/task-management/user-logs/break/end
   * Called when user unlocks from the away-lock screen, after picking which
   * break they took. `reason` is only sent for break types that require it
   * (see TMS_NO_REASON_REQUIRED_KEYS).
   */
  breakEnd(userId: string | number, code: string, breakType?: string, reason?: string): Observable<any> {
    return this.httpClientService.postTMS(
      TMS_ENDPOINTS.userLogs.breakEnd,
      {
        userId,
        code,
        ...(breakType ? { breakType } : {}),
        ...(reason ? { reason } : {}),
      }
    );
  }

  /**
   * GET /api/v1/task-management/tasks?resolutionDate=YYYY-MM-DD
   * Fetches tasks due on a given date
   */
  getTasks(resolutionDate: string, id: any, lastActiveDate?: string): Observable<any> {
    const url = `${TMS_ENDPOINTS.tasks.list}?resolutionDate=${lastActiveDate || resolutionDate}&assignedAgentId=${id}`;
    return this.httpClientService.getTMS(url);
  }

  /**
   * GET /v1/api/userMailCalendar/calendarEvents
   * Fetches calendar events for a given date range
   */
  getCalendarEvents(startDate: string, endDate: string, lastActiveDate?: string): Observable<any> {
    const url = `${TMS_ENDPOINTS.calendar.events}?startDate=${lastActiveDate || startDate}&endDate=${lastActiveDate || endDate}`;
    return this.httpClientService.get(url);
  }

  /**
   * POST /api/v1/task-management/user-logs/day/sign-off
   * Called on "Sign Off & End My Day" button click
   */
  signOff(payload: SignOffPayload): Observable<any> {
    return this.httpClientService.postTMS(
      TMS_ENDPOINTS.userLogs.signOff,
      payload
    );
  }
}